import { MapPin, Award, Briefcase, Building, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { CityCombobox } from "@/components/forms/CityCombobox";
import { MlsCombobox } from "@/components/forms/MlsCombobox";

interface Step2Props {
  data: {
    location: string;
    licenseNumber: string;
    licenseState: string;
    yearsInBusiness: number;
    mlsAffiliation: string;
  };
  onUpdate: (data: any) => void;
  onNext: () => void;
  onBack?: () => void;
}

const states = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
];

const OnboardingStep2 = ({ data, onUpdate, onNext, onBack }: Step2Props) => {
  const getYearsLabel = (years: number) => {
    if (years === 0) return "New to the industry";
    if (years === 1) return "1 year";
    if (years >= 20) return "20+ years";
    return `${years} years`;
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold text-foreground">Tell us about yourself</h2>
        <p className="text-muted-foreground">Help us personalize your experience</p>
      </div>

      <div className="space-y-5">
        {/* Location */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-primary" />
            Location
          </Label>
          <CityCombobox
            value={data.location}
            onChange={(location) => onUpdate({ ...data, location })}
            placeholder="City, State (e.g., Austin, TX)"
          />
        </div>

        {/* License */}
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2 space-y-2">
            <Label htmlFor="license" className="flex items-center gap-2">
              <Award className="h-4 w-4 text-primary" />
              License number
            </Label>
            <Input
              id="license"
              placeholder="License #"
              value={data.licenseNumber}
              onChange={(e) => onUpdate({ ...data, licenseNumber: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="state">State</Label>
            <select
              id="state"
              value={data.licenseState}
              onChange={(e) => onUpdate({ ...data, licenseState: e.target.value })}
              className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">Select</option>
              {states.map((state) => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Years in Business */}
        <div className="space-y-4">
          <Label className="flex items-center gap-2">
            <Briefcase className="h-4 w-4 text-primary" />
            Years in business
          </Label>
          <div className="bg-muted/50 rounded-xl p-4">
            <div className="flex justify-between items-center mb-4">
              <span className="text-2xl font-bold text-foreground">
                {getYearsLabel(data.yearsInBusiness)}
              </span>
            </div>
            <Slider
              value={[data.yearsInBusiness]}
              onValueChange={(value) => onUpdate({ ...data, yearsInBusiness: value[0] })}
              min={0}
              max={20}
              step={1}
              className="py-2"
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-2">
              <span>New</span>
              <span>5 yrs</span>
              <span>10 yrs</span>
              <span>15 yrs</span>
              <span>20+</span>
            </div>
          </div>
        </div>

        {/* MLS Affiliation */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Building className="h-4 w-4 text-primary" />
            Current MLS affiliation
          </Label>
          <MlsCombobox
            value={data.mlsAffiliation}
            onChange={(mls) => onUpdate({ ...data, mlsAffiliation: mls })}
            placeholder="e.g., Austin Board of REALTORS"
          />
        </div>
      </div>

      <div className="flex gap-3">
        {onBack && (
          <Button variant="outline" className="flex-1" onClick={onBack}>
            Back
          </Button>
        )}
        <Button className="flex-1 gap-2" onClick={onNext}>
          Continue
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default OnboardingStep2;
